import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	FormControl,
	IconButton,
	IconButtonProps,
	InputLabel,
	MenuItem,
	Select,
	styled,
	TextField,
} from '@mui/material'
import * as React from 'react'
import { useCallback, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DatePicker } from '@mui/x-date-pickers'
import { useInspectionsLogContext } from './InspectionsLogContext'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
dayjs.extend(timezone)

interface ExpandMoreProps extends IconButtonProps {
	expand: boolean
}
const ExpandMore = styled((props: ExpandMoreProps) => {
	const { expand, ...other } = props
	return <IconButton {...other} />
})(({ theme, expand }: { theme: any; expand: boolean }) => ({
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest,
	}),
	transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
}))

export default function FilterHeader() {
	const [expanded, setExpanded] = React.useState(true)
	const { assignees, requesters, filters, setFilters } = useInspectionsLogContext()
	const { projectTimeZone } = useProjectFeaturesContext()

	const handleExpandClick = useCallback(() => {
		setExpanded((prev) => !prev)
	}, [])

	const handleFilterChange = useCallback((field: string, value: any) => {
		console.log('handleFilterChange', field, value)
		setFilters((prev) => ({
			...prev,
			[field]: value,
		}))
	}, [setFilters])

	const handleDateChange = useCallback(
		(field: string, date: Dayjs | null) => {
			if (date) {
				const utcDate = date.tz(projectTimeZone || 'UTC').utc()
				handleFilterChange(field, utcDate)
			} else {
				handleFilterChange(field, null)
			}
		},
		[projectTimeZone, handleFilterChange],
	)

	const handleClearFilters = useCallback(() => {
		setFilters({
			requestId: null,
			page: 1,
			searchWorkItem: '',
			assignedToId: null,
			requestedById: null,
			inspectedOn: null,
			requestedOn: null,
			status: null,
		})
	}, [setFilters])

	return (
		<Card sx={{ maxHeight: '20%', my: 2 }} elevation={2}>
			<CardHeader
				sx={{ py: 0, cursor: 'pointer' }}
				subheader={
					<>
						Filters
						<ExpandMore
							expand={expanded}
							onClick={handleExpandClick}
							aria-expanded={expanded}
							aria-label='show more'
						>
							<ExpandMoreIcon />
						</ExpandMore>
					</>
				}
				onClick={handleExpandClick}
			/>
			<CardContent sx={{ py: 0, '&:last-child': { pb: 2 } }}>
				<Collapse in={expanded} timeout={200} sx={{ pb: 1 }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 2,
							width: '100%',
							p: 0,
						}}
					>
						<Box sx={{ flex: '0 1 5%' }}>
							<TextField
								label='ID'
								variant='filled'
								fullWidth
								size='small'
								type='number'
								value={filters.requestId ?? ""}
								onChange={(e) => handleFilterChange('requestId', e.target.value)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 17%' }}>
							<TextField
								label='Work Item Name'
								variant='filled'
								fullWidth
								size='small'
								value={filters.searchWorkItem}
								onChange={(e) => handleFilterChange('searchWorkItem', e.target.value)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<Autocomplete
								disablePortal
								options={assignees}
								value={assignees.find((a) => a.id === filters.assignedToId) ?? null}
								getOptionLabel={(option) => option.fullName}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								onChange={(_, newValue) =>
									handleFilterChange('assignedToId', newValue ? newValue.id : null)
								}
								renderInput={(params) => (
									<TextField {...params} label='Assigned To' variant='filled' size='small' />
								)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<Autocomplete
								disablePortal
								options={requesters}
								value={requesters.find((r) => r.id === filters.requestedById) ?? null}
								getOptionLabel={(option) => option.fullName}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								onChange={(_, newValue) =>
									handleFilterChange('requestedById', newValue ? newValue.id : null)
								}
								renderInput={(params) => (
									<TextField {...params} label='Requested By' variant='filled' size='small' />
								)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<DatePicker
								label='Inspected On'
								value={filters.inspectedOn}
								onChange={(date: Dayjs | null) => handleDateChange('inspectedOn', date)}
								renderInput={(props) => <TextField {...props} variant='filled' size='small' />}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<DatePicker
								label='Requested On'
								value={filters.requestedOn}
								onChange={(date: Dayjs | null) => handleDateChange('requestedOn', date)}
								renderInput={(props) => <TextField {...props} variant='filled' size='small' />}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<FormControl variant='filled' fullWidth>
								<InputLabel id='status-label'>Status</InputLabel>
								<Select
									labelId='status-label'
									value={filters.status ?? ''}
									onChange={(e) => handleFilterChange('status', e.target.value)}
									size='small'
								>
									{['Pending', 'Passed', 'Rework'].map((x, index) => (
										<MenuItem value={x} key={index}>
											{x}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<Box sx={{ flex: '0 1 7%', display: 'flex' }}>
							<Button
								onClick={handleClearFilters}
								variant='text'
								sx={{
									textTransform: 'none',
									color: 'gray',
									textDecoration: 'underline',
									'&:hover': {
										textDecoration: 'none',
										color: 'darkblue',
									},
								}}
							>
								Clear Filters
							</Button>
						</Box>
					</Box>
				</Collapse>
			</CardContent>
		</Card>
	)
}
